import * as Yup from 'yup';
import { ModalComponent } from '../../../../components/layout/modals/ModalComponent';
import { Formik } from 'formik';
import { Fragment } from 'react';
import { FormField as FormikFormField } from '../../../../components/layout/form-fields/FormField';
import React from 'react';
import { jobPositionStore } from '../JobPositionStore';
import { TimezoneDropdown } from 'components/timezones/shared/TimezoneDropdown';
import { LocationDropdown } from 'components/layout/form-fields/inputs/LocationDropdown';
import { authStore } from '../../../../shared/security/AuthStore';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import {
	FormField,
	FormRow,
	FormSection
} from 'components/layout/modals/components';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { ClientsDropdown } from 'components/layout/form-fields/inputs/ClientsDropdown';
import { ClientContactsDropdown } from 'components/layout/form-fields/inputs/ClientContactsDropdown';
import UserAssignmentInput from 'ats/components/user-assignment-input/UserAssignmentInput';
import MultiDropdownEditable from 'ats/components/multidropdown-editable/MultiDropdownEditable';
import { JobRoleDropdown } from 'components/layout/form-fields/inputs/JobRoleDropdown';

const ValidationSchema = Yup.object().shape({
	title: Yup.string().required().label('Title'),
	timezones: Yup.array().nullable().label('Timezones'),
	locations: Yup.array().nullable().label('Locations'),
	clientId: Yup.string().required().label('Client'),
	owners: Yup.array().nullable().label('Owner'),
	hiringManagerId: Yup.string().nullable().label('Hiring Manager'),
	accountManagerId: Yup.string().required().label('Account Manager'),
	talentLeaderId: Yup.string().nullable().label('Talent Leader'),
	priority: Yup.string().required().label('Priority'),
	urgency: Yup.string().label('Urgency'),
	roleId: Yup.number().nullable().label('Role')
});

export class JobPositionEdit extends ModalComponent {
	static isV2 = true;

	getInitialValues() {
		const timezones = (this.props.job.timezones || []).map(item => {
			return item.timezone;
		});
		const locations = (this.props.job.locations || []).map(item => {
			return item.location;
		});

		const owners = (this.props.job.owners || []).map(item => {
			return { ...item.owner, isPrimary: item.isPrimary };
		});

		return {
			title: this.props.job.title,
			priority: this.props.job.priority,
			urgency: this.props.job.urgency,
			timezones,
			locations,
			client: this.props.job.client,
			clientId: this.props.job.client?.id,
			owners: owners,
			hiringManager: this.props.job.hiringManager,
			hiringManagerId: this.props.job.hiringManager?.id,
			accountManager: this.props.job.accountManager,
			accountManagerId: this.props.job.accountManager?.id,
			talentLeader: this.props.job.talentLeader,
			talentLeaderId: this.props.job.talentLeader?.id,
			roleId: this.props.job.role?.id,
			role: this.props.job.role
		};
	}

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				onSubmit={values => {
					this.saving();
					this.save(values);
				}}>
				{formikBag => this.renderModalContainer(formikBag, 'JobPositionEdit')}
			</Formik>
		);
	}

	renderForm(formikBag) {
		return (
			<Fragment>
				<FormRow>
					<FormSection>
						<FormField
							label="Title"
							required
							variant="dashed"
							error={formikBag.errors.title}>
							<FormikFormField
								className="has-text-left"
								field="title"
								type="text"
								placeholder=" "
								maxlength={255}
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Client"
							required
							variant="dashed"
							error={formikBag.errors.clientId}>
							<ClientsDropdown
								value={formikBag.values.client}
								placeholder=" "
								isClearable
								onValueChange={value => {
									formikBag.setFieldValue('client', value);
									formikBag.setFieldValue('clientId', value?.id);

									formikBag.setFieldValue('hiringManager', null);
									formikBag.setFieldValue('hiringManagerId', null);
								}}
							/>
						</FormField>
						<FormField
							label="Hiring Manager"
							variant="dashed"
							error={formikBag.errors.hiringManagerId}>
							<ClientContactsDropdown
								value={formikBag.values.hiringManager}
								placeholder=" "
								isClearable
								clientId={formikBag.values.clientId}
								disabled={!formikBag.values.client}
								onValueChange={value => {
									formikBag.setFieldValue('hiringManager', value);
									formikBag.setFieldValue('hiringManagerId', value?.id ?? null);
								}}
							/>
						</FormField>
						<FormField
							label="Urgency"
							variant="dashed"
							error={formikBag.errors.urgency}>
							<Dropdown
								value={formikBag.values.urgency}
								placeholder=" "
								onChange={item => {
									formikBag.setFieldValue('urgency', item);
								}}
								options={jobPositionStore.urgencyOptions}
								optionValue="value"
							/>
						</FormField>
						<FormField
							label="Priority"
							required
							variant="dashed"
							error={formikBag.errors.priority}>
							<Dropdown
								value={formikBag.values.priority}
								placeholder=" "
								onChange={item => {
									formikBag.setFieldValue('priority', item);
								}}
								options={jobPositionStore.priorityOptions}
								optionValue="value"
							/>
						</FormField>
						<FormField
							label="Role"
							variant="dashed"
							error={formikBag.errors.roleId}>
							<JobRoleDropdown
								isMulti={false}
								value={formikBag.values.role}
								placeholder=" "
								styleVersion="ats"
								onValueChange={value => {
									formikBag.setFieldValue('role', value);
									formikBag.setFieldValue('roleId', value.id);
								}}
							/>
						</FormField>
					</FormSection>
					<FormSection title="Details" size={2}>
						<FormField
							label="Account Manager"
							required
							error={formikBag.errors.accountManagerId}>
							<UserAssignmentInput
								value={formikBag.values.accountManager}
								placeholder=" "
								role={['Account Manager']}
								minSearchChars={0}
								onChange={value => {
									formikBag.setFieldValue('accountManager', value);
									formikBag.setFieldValue(
										'accountManagerId',
										value?.id || null
									);
								}}
							/>
						</FormField>
						<FormField
							label="Talent Leader"
							error={formikBag.errors.talentLeaderId}>
							<UserAssignmentInput
								value={formikBag.values.talentLeader}
								placeholder=" "
								role={['Talent Leader']}
								minSearchChars={0}
								onChange={value => {
									formikBag.setFieldValue('talentLeader', value);
									formikBag.setFieldValue('talentLeaderId', value?.id || null);
								}}
							/>
						</FormField>
						<FormField label="Owner" error={formikBag.errors.owners}>
							<UserAssignmentInput
								value={formikBag.values.owners}
								isMulti
								placeholder=" "
								role={NON_CANDIDATE_USER_ROLES}
								onChange={values => {
									formikBag.setFieldValue('owners', values);
								}}
								onAssignToMe={() => {
									const currentUser = authStore.profile;
									const currentOwners = formikBag.values.owners || [];

									// Check if current user is already in the owners list
									const isAlreadyOwner = currentOwners.some(
										owner => owner.id === currentUser.id
									);

									if (!isAlreadyOwner) {
										formikBag.setFieldValue('owners', [
											...currentOwners,
											{
												...currentUser,
												isPrimary: currentOwners.length === 0
											}
										]);
									}
								}}
							/>
						</FormField>
						<FormField label="Locations" error={formikBag.errors.locations}>
							<MultiDropdownEditable
								values={formikBag.values.locations}
								textKey="name"
								onRemoveValue={value => {
									formikBag.setFieldValue(
										'locations',
										formikBag.values.locations.filter(v => v.id !== value.id)
									);
								}}>
								<LocationDropdown
									isMulti
									value={formikBag.values.locations}
									placeholder=" "
									onValueChange={values => {
										formikBag.setFieldValue('locations', values);
									}}
								/>
							</MultiDropdownEditable>
						</FormField>
						<FormField label="Timezones" error={formikBag.errors.timezones}>
							<MultiDropdownEditable
								values={formikBag.values.timezones}
								textKey="text"
								onRemoveValue={value => {
									formikBag.setFieldValue(
										'timezones',
										formikBag.values.timezones.filter(v => v.id !== value.id)
									);
								}}>
								<TimezoneDropdown
									isMulti
									value={formikBag.values.timezones}
									placeholder=" "
									onValueChange={values => {
										formikBag.setFieldValue('timezones', values);
									}}
									usePurpleStyles
								/>
							</MultiDropdownEditable>
						</FormField>
					</FormSection>
				</FormRow>
			</Fragment>
		);
	}

	async save(values) {
		const {
			title,
			urgency,
			priority,
			role,
			roleId,
			client,
			clientId,
			hiringManager,
			hiringManagerId,
			accountManager,
			accountManagerId,
			talentLeader,
			talentLeaderId,
			owners,
			locations,
			timezones
		} = values;

		const jobData = {
			title,
			urgency,
			priority,
			role,
			roleId,
			client,
			clientId,
			hiringManager,
			hiringManagerId,
			accountManager,
			accountManagerId,
			talentLeader,
			talentLeaderId
		};

		jobPositionStore
			.update(this.props.job.id, {
				job: jobData,
				owners,
				locations,
				timezones
			})
			.then(result => {
				this.resultNotification(result, 'Job Opening', 'Edit');
			});
	}
}
